import { Component, lazy, Suspense, useEffect } from "react";
import { isIOS } from "react-device-detect";
import "react-notifications-component/dist/theme.css";
import { connect, Provider } from "react-redux";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { init as initAnalytics, RouteAnalytics } from "./Analytics";
import "./App.css";
import { AppContext, AppMode, CasinoGameInfo } from "./http/protocol";
import { getContainer } from "./ioc/IOCSetup";
import { AppState, configureStore, useAppSelector } from "./RootReducer";
import RouteChanges from "./RouteChanges";
import { SettingsService } from "./settings/SettingsService";
import { ToastHandler } from "./toasts/toasts";
import BalanceUpdater from "./ui/BalanceUpdater";
import CashierModal from "./ui/CashierModal";
import CountryBlocked from "./ui/CountryBlocked";
import FreshChatWidget from "./ui/FreshChatWidget";
import LoginModal from "./ui/LoginModal";
import NotificationsModal from "./ui/notifications/NotificationsModal";
import UserDetails from "./ui/UserDetails";
import WindowSizeContainer from "./ui/WindowSizeContainer";
import VerifyEmailByLinkAccount from "./onboardingpage/VerifyEmailByLink";

import { AccountPage } from "./account/AccountPage";
import { MobileGamePreview } from "./casino/ui/MobileGamePreview";

import { GlobalContentComponent } from "./contenful/GlobalContentComponent";
import { PopupComponent } from "./popup/PopupComponent";

import { Maintenance } from "./Maintenance";
import { ReCaptcha } from "./ReCaptcha";
import { FreshChatButton } from "./ui/FreshChatButton";

import { JackpotData } from "./casino/JackpotData";
import { CountryBlock } from "./CountryBlock";
import UserModalController from "./onboardingpage/modalview/UserModalController";
import SportsView from "./sports/SportsView";
import { SuspenseLoaders } from "./SuspenseLoaders";
import MayaPaymentAuth from "./ui/paymentproviders/MayaPaymentAuth";
import LastVisitedRoute from "./user/LastVisitedRoute";
import { HeaderContributor } from "./utils/HeaderContributor";
import Widgets from "./Widgets";
import MenusLayout from "./ui/navigation/MenuLayout";
import MayaMiniAppLogin from "./mayaminiapp/MayaMiniAppLogin";
import ErrorPage from "./errorpage/ErrorPage";
import RoutingUtils from "./routing/RoutingUtils";
import BlinkCasinoGameView from "./casino/ui/game/blink/BlinkCasinoGameView";
import Sportsbook from "./sports/Sportsbook";
import SportsbooksProvider from "./sports/SportsbooksProvider";
import { PopupStatus } from "./popup/PopupReducer";
import GlifeDisclaimerPopup from "./glife/GlifeDisclaimerPopup";
import GlifeResetPassword from "./glife/GlifeResetPassword";
import { GlifeLandingPage } from "./glife/GlifeLandingPage";
import { ContextClass, getContextClass } from "./utils/utils";

const BrandStyles = lazy(() => import("./brand/BrandStyles"));
const LandingPage = lazy(() => import("./landingpage/LandingPage"));
const CasinoPageWrapper = lazy(() => import("./casino/ui/CasinoPage"));
const RacingView = lazy(() => import("./racing/RacingView"));
const ExitPayment = lazy(() => import("./ui/paymentproviders/ExitPayment"));
const PokerDesktop = lazy(() => import("./poker/PokerDesktop"));
const OnboardingAppView = lazy(() => import("./onboardingpage/modalview/OnboardingAppView"));
const PlayPoker = lazy(() => import("./poker/PlayPoker"));
const ExitSports = lazy(() => import("./sports/ExitSports"));
const PokerView = lazy(() => import("./poker/PokerView"));
const RegisterSuccess = lazy(() => import("./user/ui/RegisterSuccess"));
const SportsViewPlaceholder = lazy(() => import("./sports/SportsViewPlaceholder"));
const Withdraw = lazy(() => import("./ui/Withdraw"));
const ResetPasswordPage = lazy(() => import("./onboardingpage/resetPasswordPage/ResetPasswordPage"));
const ExitCasinoGame = lazy(() => import("./casino/ui/game/ExitCasinoGame"));
const CasinoGameView = lazy(() => import("./casino/ui/game/CasinoGameView"));
const ExitPoker = lazy(() => import("./poker/ExitPoker"));
const ChooseScreenNameView = lazy(() => import("./user/ui/ChooseScreenNameView"));
const InfoPageView = lazy(() => import("./ui/InfoPageView"));
const MfaPage = lazy(() => import("./profilepage/MfaPage"));
const PromoPageView = lazy(() => import("./promopage/PromoPageView"));
const NewsPage = lazy(() => import("./news/NewsPage"));
const ArticleView = lazy(() => import("./news/ArticleView"));
const NotFoundPage = lazy(() => import("./notfoundpage/NotFoundPage"));
const MayaMiniAppView = lazy(() => import("./mayaminiapp/MayaMiniAppView"));
const SportsBook = lazy(() => import("./sports/Sportsbook"));
const GlifeView = lazy(() => import("./glife/GlifeView"));
const GlifeExternalLinkView = lazy(() => import("./glife/GlifeExternalLinkView"));
const GlifeAppLogin = lazy(() => import("./glife/GlifeAppLogin"));

//library.add(faBars, faEye, faEyeSlash, faChevronLeft);
type IosIcon = {
  type: string;
  size: string;
  href: string;
};
type IosSplashscreen = {
  type: string;
  width: string;
  height: string;
  pixelRatio: string;
  href: string;
};

type IosMedia = {
  icons: IosIcon[];
  splashScreens: IosSplashscreen[];
};

interface Props {
  locale?: string;
  mobile?: boolean;
  loggedIn?: boolean;
  mobilePreviewGame?: CasinoGameInfo;
  renderSportsView?: boolean;
  isBlocked?: boolean;
  isGlifeDisclaimerShowing?: boolean;
  appContext?: AppContext;
  appMode?: AppMode,
}

interface State {}
export const App = (props: { locale?: string }) => {
  useEffect(() => {
    let loader = document.getElementById("jsLoader");
    if (loader) {
      loader.style.display = "none";
    }
  }, [1]);
  return (
    <Provider store={configureStore()}>
      <AppWrapper locale={props.locale} />
    </Provider>
  );
};

class AppWrapperImpl extends Component<Props, State> {
  settings: SettingsService;
  constructor(props: Props) {
    super(props);
    this.settings = getContainer().getSettingsService();
  }

  componentDidMount() {
    const attributes: string = "width=device-width, initial-scale=1, shrink-to-fit=no, maximum-scale=1, user-scalable=no";
    const existingMetaViewport: NodeListOf<HTMLMetaElement> = document.querySelectorAll('meta[name="viewport"]');
    const explicitAppModeParam = new URLSearchParams(document.location.search).get("appMode");
    const explicitAppMode = AppMode[explicitAppModeParam as keyof typeof AppMode];

    if (explicitAppMode == AppMode.EMBEDDED || explicitAppMode == AppMode.NATIVE) {
      existingMetaViewport.forEach((_, i) => {
        existingMetaViewport[i].remove();
      });

      let metaViewport = document.createElement("meta");
      metaViewport.content = attributes;
      metaViewport.id = "viewport";
      metaViewport.name = "viewport";
      document.getElementsByTagName("head")[0].appendChild(metaViewport);

      let appMode;
      if (explicitAppMode) {
        appMode = explicitAppMode;
      } else {
        appMode = AppMode.NONE;
      }

      localStorage.setItem("AppMode", appMode);
      document.body.classList.add(`mode-${appMode.toLowerCase()}`);
    }

    isIOS && this.setIosMedia();
    if (!document.location.pathname.includes("poker")) {
      localStorage.setItem("loadPoker", "false");
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (this.props.appContext !== undefined) {
      let { particular, common }: ContextClass = getContextClass(this.props.appContext, this.props.appMode);
      document.body.classList.add(particular);
      document.body.classList.add(common);
    }
  }

  componentWillUnmount(): void {
    localStorage.removeItem("AppMode");
  }

  setIosMedia() {
    let iosMediaJSON: IosMedia = this.settings.settingsData && this.settings.settingsData.iosMediaJSON && JSON.parse(this.settings.settingsData.iosMediaJSON);
    //Set app icon
    let iconLink: HTMLLinkElement = document.createElement("link");
    iconLink.rel = "apple-touch-icon";
    if (iosMediaJSON && iosMediaJSON.icons && iosMediaJSON.icons.length > 0 && iosMediaJSON.icons[0].href) {
      iconLink.href = iosMediaJSON.icons[0].href;
    }
    document.head.appendChild(iconLink);

    //set app splashscreens
    iosMediaJSON &&
      iosMediaJSON.splashScreens &&
      iosMediaJSON.splashScreens.map((screen) => {
        let splashscreen: HTMLLinkElement = document.createElement("link");
        if (screen.href) {
          splashscreen.href = screen.href;
        }
        splashscreen.media = `(device-width: ${screen.width}) and (device-height: ${screen.height}) and (-webkit-device-pixel-ratio: ${screen.pixelRatio}) and (orientation: portrait)`;
        splashscreen.rel = "apple-touch-startup-image";
        document.head.appendChild(splashscreen);
      });
    document.body.classList.add("ios");
  }

  render() {
    let settings = this.settings.settingsData;
    let mobile = this.props.mobile;
    let countryIsBlocked = this.props.isBlocked;

    return (
      <Router basename={this.props.locale ? "/" + this.props.locale : ""}>
        {settings && settings.freshChatEnabled && <FreshChatWidget />}
        {settings && settings.freshChatEnabled && <FreshChatButton />}
        <CountryBlock />
        <Maintenance />
        <RoutingUtils />
        {settings?.canonicalBaseUrl && <HeaderContributor locale={this.props.locale} canonicalBaseUrl={settings.canonicalBaseUrl} />}
        {!countryIsBlocked && (
          <>
            {settings && initAnalytics(settings) && <RouteAnalytics />}
            {settings && settings.reCaptchaSettings?.enabled && <ReCaptcha siteKey={settings.reCaptchaSettings.siteKey} />}
            {settings?.casinoEnabled && <JackpotData />}
            {this.props.loggedIn && <CashierModal />}
            <Widgets />
            <GlobalContentComponent />
            <RouteChanges />
            <ToastHandler />
            <UserDetails />
            <LoginModal />
            <NotificationsModal />
            <LastVisitedRoute />
            <BalanceUpdater />
            <WindowSizeContainer />
            <UserModalController />
            <Route exact path={["/casino/:tabId?/:name?", "/casino/:search?", "/casino/page/:pageId?/:pageName?/:tabId?/:tabName?", "/casino/category/:catId?/:catName?"]}>
              {mobile && this.props.mobilePreviewGame && !this.props.isGlifeDisclaimerShowing && <MobileGamePreview />}
            </Route>
            <Route exact path={["/", "/poker", "/casino", "/sports", "/racing"]}>
              <PopupComponent />
            </Route>
            <GlifeDisclaimerPopup />
          </>
        )}
        <Suspense fallback={<SuspenseLoaders />}>
          <Switch>
            <Route exact path="/mayaminiapp" component={MayaMiniAppView} />
            <Route exact path="/mayaminiapp/login" component={MayaMiniAppLogin} />
            <Route exact path="/glife" component={GlifeView} />
            <Route exact path="/glife/external-link" component={GlifeExternalLinkView} />
            <Route exact path="/glife/login" component={GlifeAppLogin} />
            <Route exact path="/glife/reset-password" component={GlifeResetPassword} />
            <Route exact path="/glife-landingpage" component={GlifeLandingPage} />
            <Route exact path={"/poker/play"} component={PlayPoker} />
            <Route path="/error-page/:errorType" component={ErrorPage} />
            <Route path="/">
              <MenusLayout>
                <Switch>
                  <Route exact path="/playnow/resetpassword/:address/:token" component={ResetPasswordPage} />
                  <Route exact path="/playnow/resetpassword" component={ResetPasswordPage} />
                  <Route exact path="/app" component={OnboardingAppView} />
                  <Route exact path="/blocked/:country" component={CountryBlocked} />
                  <Route exact path="/casino/play/:gameId/:name/:mode?" component={CasinoGameView} />
                  <Route exact path="/casino/originals/:gameId/:name/:mode?" component={BlinkCasinoGameView} />
                  <Route exact path={"/casino/exit"} component={ExitCasinoGame} />
                  <Route exact path={"/payment/exit/:result"} component={ExitPayment} />
                  <Route exact path={"/payment/maya/auth"} component={MayaPaymentAuth} />
                  <Route exact path={["/casino", "/casino/search", "/casino/:tabId/:tabName", "/casino/page/:pageId/:pageName/:tabId?/:tabName?", "/casino/category/:catId?/:catName?"]} component={CasinoPageWrapper} />
                  <Route exact path={"/info/:page"} component={InfoPageView} />
                  <Route exact path={"/promo/:id"} component={PromoPageView} />
                  <Route exact path={"/profile/2fa"} component={MfaPage} />
                  <Route exact path={"/poker/exit"} component={ExitPoker} />
                  <Route exact path={"/sportsbook/exit"} component={ExitSports} />
                  <Route exact path={"/register/success"} component={RegisterSuccess} />
                  <Route exact path={"/poker/desktop"} component={PokerDesktop} />
                  <Route exact path="/poker" component={PokerView} />
                  <Route exact path={"/choose-screen-name"} component={ChooseScreenNameView} />
                  {settings && settings.sportsbookEnabled && <Route exact path="/sportsbook" component={Sportsbook} />}
                  {settings && settings.sportsbookEnabled && <Route exact path="/sportsbook/:provider" component={SportsbooksProvider} />}
                  <Route exact path="/racing" component={RacingView} />
                  <Route exact path="/withdraw" component={Withdraw} />
                  <Route exact path="/news" component={NewsPage} />
                  <Route exact path="/news/:slug" component={ArticleView} />
                  <Route exact path="/" component={LandingPage} />
                  <Route exact path={"/account/:section?/:subsection?"} component={AccountPage} />
                  <Route exact path={"/brand-styles/:section?/:subsection?/:subsubsection?"} component={BrandStyles} />
                  <Route exact path={"/verifylink"} component={VerifyEmailByLinkAccount} />
                </Switch>
              </MenusLayout>
            </Route>
            <Route path={["/404", "*"]} component={NotFoundPage} />
          </Switch>
        </Suspense>
      </Router>
    );
  }
}
const AppWrapper = connect((state: AppState, own: Props) => {
  return {
    mobile: state.settings.mobile,
    locale: own.locale,
    loggedIn: state.user.user?.loggedIn,
    mobilePreviewGame: state.navigation.mobilePreviewGame,
    renderSportsView: state.navigation.sportsbookLoaded || state.navigation.sportsbookVisible,
    isBlocked: state.settings.isBlocked,
    isGlifeDisclaimerShowing: state.popup.popupStatus === PopupStatus.GLIFE_EXTERNAL_LINK_DISCLAIMER,
    appContext: state.settings.appContext,
    appMode: state.user.appMode
  };
})(AppWrapperImpl as any);

export default App;


